"use client";

import React, { useRef, useEffect, useState } from "react";
import Sketch from "react-p5";
import p5Types from "p5";
import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { useFormContext } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: p5Types.Color;
  isEU: boolean;
}
function TradeRequirementForm() {
  const {
    control,
    handleSubmit: hookHandleSubmit
  } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const successMessage = "Thank you! Your trade requirement has been submitted successfully. Our team will review your details and contact you within 24 hours.";
  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      if (typeof window !== 'undefined' && (window as any).wvcClient?.formSubmission) {
        await (window as any).wvcClient.formSubmission({
          formId: "trade-requirement-hero",
          sectionName: "TheDigitalTradeBridge",
          formData: data,
          targetEmail: "shivanshpaliwal0005@gmail.com"
        });
        setIsSubmitted(true);
      } else {
        const response = await fetch("/api/forms/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            formId: "trade-requirement-hero",
            sectionName: "TheDigitalTradeBridge",
            formData: data,
            targetEmail: "shivanshpaliwal0005@gmail.com"
          })
        });
        if (!response.ok) {
          throw new Error("Fallback submission failed");
        }
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error("Form submission error details:", error);
      setSubmitError("Failed to submit form. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="p-6 text-center space-y-4">
        <div className="size-12 bg-secondary/20 rounded-full flex items-center justify-center mx-auto">
          <ArrowRight className="text-secondary rotate-[-45deg]" />
        </div>
        <p className="text-lg font-medium text-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={hookHandleSubmit(onSubmit)}>
      <div className="space-y-4 pt-4">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="company" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter your company name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="contact" rules={{
          required: "Contact number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Contact Number</FormLabel>
                <FormControl>
                  <Input placeholder="+1 234 567 890" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
        <FormField control={control} name="requirements" rules={{
        required: "Please describe your requirements"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Type of Requirements</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your trade requirements or expansion plans..." className="min-h-[100px]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base font-bold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : 'Submit Requirement'}
        </Button>
      </div>
    </Form>;
}
export default function TheDigitalTradeBridge() {
  const containerRef = useRef<HTMLDivElement>(null);
  const particles = useRef<Particle[]>([]);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);
  const setup = (p5: p5Types, canvasParentRef: Element) => {
    if (dimensions.width === 0) return;
    p5.createCanvas(dimensions.width, dimensions.height).parent(canvasParentRef);
    particles.current = [];
    for (let i = 0; i < 80; i++) {
      createParticle(p5, true);
    }
  };
  const createParticle = (p5: p5Types, initial = false) => {
    const isEU = p5.random() > 0.5;
    const isDesktop = p5.width >= 768;
    let x, y, vx, vy;
    if (isDesktop) {
      y = p5.random(p5.height);
      x = initial ? p5.random(p5.width) : isEU ? -10 : p5.width + 10;
      vx = isEU ? p5.random(1, 3) : p5.random(-3, -1);
      vy = p5.random(-0.5, 0.5);
    } else {
      x = p5.random(p5.width);
      y = initial ? p5.random(p5.height) : isEU ? -10 : p5.height + 10;
      vy = isEU ? p5.random(1, 3) : p5.random(-3, -1);
      vx = p5.random(-0.5, 0.5);
    }
    const size = p5.random(2, 6);
    const color = isEU ? p5.color(255, 255, 255, p5.random(100, 200)) : p5.color(255, 191, 0, p5.random(100, 200));
    particles.current.push({
      x,
      y,
      vx,
      vy,
      size,
      color,
      isEU
    });
  };
  const draw = (p5: p5Types) => {
    p5.clear();
    p5.noStroke();
    for (let i = particles.current.length - 1; i >= 0; i--) {
      const p = particles.current[i];
      p.x += p.vx;
      p.y += p.vy;
      p5.fill(p.color);
      p5.ellipse(p.x, p.y, p.size);
      const isDesktop = p5.width >= 768;
      let isOffScreen = false;
      if (isDesktop) {
        isOffScreen = p.isEU && p.x > p5.width + 10 || !p.isEU && p.x < -10 || p.y < -10 || p.y > p5.height + 10;
      } else {
        isOffScreen = p.isEU && p.y > p5.height + 10 || !p.isEU && p.y < -10 || p.x < -10 || p.x > p5.width + 10;
      }
      if (isOffScreen) {
        particles.current.splice(i, 1);
        createParticle(p5);
      }
    }
  };
  const windowResized = (p5: p5Types) => {
    if (containerRef.current) {
      p5.resizeCanvas(containerRef.current.offsetWidth, containerRef.current.offsetHeight);
    }
  };
  return <section data-section-id="82032" id="the-eu-india-gateway" ref={containerRef} className="relative w-full min-h-[100svh] flex items-center justify-center overflow-hidden bg-background">
      {}
      <div className="absolute inset-0 flex flex-col md:flex-row z-0">
        {}
        <div className="relative w-full h-1/2 md:w-1/2 md:h-full bg-primary overflow-hidden">
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/eu_skyline_architectural_blue.png)] bg-cover bg-center opacity-25 mix-blend-overlay transition-transform duration-1000 hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-primary/90 to-transparent" />
        </div>
        
        {}
        <div className="relative w-full h-1/2 md:w-1/2 md:h-full bg-secondary overflow-hidden">
          <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577832/img/india_textile_pattern_saffron.png)] bg-cover bg-center opacity-40 mix-blend-soft-light transition-transform duration-1000 hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-l from-secondary/85 to-transparent" />
        </div>
      </div>

      {}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {dimensions.width > 0 && <Sketch setup={setup} draw={draw} windowResized={windowResized} />}
      </div>

      {}
      <div className="relative z-20 flex flex-col items-center justify-center text-center px-6 py-20 max-w-6xl mx-auto pointer-events-none">
        <div className="space-y-8 backdrop-blur-md bg-white/5 p-8 md:p-14 rounded-xl border border-white/20 shadow-2xl">
          <h1 className="text-5xl md:text-7xl lg:text-[80px] font-serif font-semibold text-white leading-[1.1] tracking-tight drop-shadow-lg">
            Connecting European and Indian Businesses <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-white">for Trade, Expansion, and Strategic Partnerships</span>
          </h1>
          
          <p className="text-lg md:text-2xl text-white/95 max-w-3xl mx-auto font-sans font-medium drop-shadow-sm">
            We help companies across Europe and India find reliable partners, enter new markets, manage trade opportunities, and build long‑term cross‑border business relationships.
          </p>

          <div className="pt-8 pointer-events-auto flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => setIsFormOpen(true)} size="lg" className="h-14 px-10 text-lg bg-white text-primary hover:bg-secondary hover:text-secondary-foreground shadow-xl transition-all duration-300 rounded-sm font-sans font-bold group">
              Book a Consultation
              <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button onClick={() => setIsFormOpen(true)} variant="outline" size="lg" className="h-14 px-10 text-lg border-white text-white bg-transparent hover:bg-white hover:text-primary shadow-xl transition-all duration-300 rounded-sm font-sans font-bold">
              Submit Your Trade Requirement
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-[500px] bg-background border-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif font-semibold text-primary">Trade Requirement Form</DialogTitle>
            <DialogDescription>
              Provide your details below to connect with our trade experts. We'll review your requirements and get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <FormProvider formId="trade-requirement-hero" sectionName="TheDigitalTradeBridge" targetEmail="shivanshpaliwal0005@gmail.com" defaultValues={{
          name: "",
          company: "",
          contact: "",
          email: "",
          requirements: ""
        }}>
            <TradeRequirementForm />
          </FormProvider>
        </DialogContent>
      </Dialog>
    </section>;
}